import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "./index";
import { StoreStatus } from "./StoreStatus";
import { initialStoreStateBase, StoreStateBase } from "./StoreStateBase";
import { PromoCodeClientMeRepository } from "../infrastructure/PromoCodesSlice/PromoCodeClientMeRepository";
import { PromoCodeRepository } from "../infrastructure/PromoCodesSlice/PromoCodeRepository";
import type { PromoCodeGet } from "../domain/PromoCodesSlice/PromoCodeGet";
import { bestOrderPromo, meetsMinOrder, computeLineDiscount, bestOrderPromoReadOnly } from "../application/PromoCodesSlice/rules";
import globalRoles from "../globalRoles";

export type ApplyPromoResult =
  | { status: "applied"; promo: PromoCodeGet }
  | { status: "not_applicable"; promo: PromoCodeGet; remaining: number }
  | { status: "invalid" };

export interface PromosState extends StoreStateBase {
  promos: PromoCodeGet[];
  appliedPromo: PromoCodeGet | null;
}

const initialState: PromosState = {
  ...initialStoreStateBase,
  promos: [],
  appliedPromo: null,
};

const repoFor = (isLoggedIn: boolean) =>
  isLoggedIn ? new PromoCodeClientMeRepository() : new PromoCodeRepository();

// ==== selectors
export const selectPromos = (s: RootState) => s.promos.promos;
export const selectAppliedPromo = (s: RootState) => s.promos.appliedPromo;
export const selectPromosStatus = (s: RootState) => s.promos.storeStatus;

// ==== thunks
export const doPromosFetchVisible = createAsyncThunk<
  PromoCodeGet[] | null,
  void,
  { state: RootState }
>("promos/fetchVisible", async (_arg, { getState }) => {
  const state = getState();
  const isLogged = state.security.isLoggedIn && state.security.roles.includes(globalRoles.CLIENT);
  try {
    const list = await repoFor(isLogged).fetchAllVisible();
    return list ?? [];
  } catch {
    return [];
  }
});

export const doPromosValidateAndMaybeApply = createAsyncThunk<
  ApplyPromoResult,
  { code: string; subtotal: number },
  { state: RootState }
>("promos/validate", async ({ code, subtotal }, { getState }) => {
  const state = getState();
  const isLogged = state.security.isLoggedIn && state.security.roles.includes(globalRoles.CLIENT);
  const trimmed = code.trim();
  if (!trimmed) return { status: "invalid" };

  const result: PromoCodeGet | null = await repoFor(isLogged).validatePromo({
    code: trimmed,
  });

  if (!result || !result.isActive) return { status: "invalid" };

  const min = result.minimumOrderAmount ?? 0;
  if (subtotal < min) {
    return {
      status: "not_applicable",
      promo: result,
      remaining: Math.max(min - subtotal, 0),
    };
  }

  return { status: "applied", promo: result };
});

// ==== slice
export const promosSlice = createSlice({
  name: "promos",
  initialState,
  reducers: {
    reset: (s) => {
      s.appliedPromo = null;
      s.promos = [];
      s.storeStatus = StoreStatus.idle;
      s.error = null;
    },
    clearPromo(s) {
      s.appliedPromo = null;
    },
    setAppliedPromo(s, a: PayloadAction<PromoCodeGet | null>) {
      s.appliedPromo = a.payload;
    },
  },
  extraReducers: (b) => {
    b.addCase(doPromosFetchVisible.pending, (s) => {
      s.storeStatus = StoreStatus.loading;
    })
      .addCase(doPromosFetchVisible.fulfilled, (s, a) => {
        s.storeStatus = StoreStatus.succeeded;
        s.promos = a.payload ?? [];
      })
      .addCase(doPromosFetchVisible.rejected, (s, a) => {
        s.storeStatus = StoreStatus.failed;
        s.error = String(a.error.message ?? "Fetch promos failed");
        s.promos = [];
      })
      .addCase(doPromosValidateAndMaybeApply.fulfilled, (s, a) => {
        if (a.payload.status === "applied") {
          s.appliedPromo = a.payload.promo;
          s.promos = []; // identical behavior: clear visible promos on apply
        }
      });
  },
});

export default promosSlice.reducer;
export const { clearPromo, setAppliedPromo } = promosSlice.actions;

// ==== helpers used by the cart slice/hook (pure—same logic as your hook)
export const getOrderPromo = (promos: PromoCodeGet[], applied: PromoCodeGet | null, subtotal: number) => {
  if (applied && applied.appliesToOrderTotal && meetsMinOrder(applied, subtotal)) {
    return applied;
  }
  return bestOrderPromo(promos, subtotal) ?? null;
};

export const getOrderPromoReadOnly = (promos: PromoCodeGet[], subtotal: number) => {
  return bestOrderPromoReadOnly(promos, subtotal) ?? null;
};

export const getLinePromos = (promos: PromoCodeGet[], applied: PromoCodeGet | null): PromoCodeGet[] => {
  const list: PromoCodeGet[] = [];
  if (applied && !applied.appliesToOrderTotal) list.push(applied);
  const visibleLines = promos.filter((p) => !p.appliesToOrderTotal);
  return list.concat(visibleLines);
};

export const getPromoForProductBadge = (promos: PromoCodeGet[], applied: PromoCodeGet | null, productId: number): PromoCodeGet | null => {
  const candidates = [
    ...(applied && !applied.appliesToOrderTotal ? [applied] : []),
    ...promos.filter((p) => !p.appliesToOrderTotal),
  ].filter((p) => p.isActive && p.productPromoCodes?.some((pp) => pp.productId === productId));
  if (candidates.length === 0) return null;
  return candidates
    .sort((a, b) => {
      const av = (a.discountAmount ?? 0) || (a.discountPercentage ?? 0);
      const bv = (b.discountAmount ?? 0) || (b.discountPercentage ?? 0);
      return bv - av;
    })[0];
};

export const getPromoForProductPricing = (
  promos: PromoCodeGet[],
  applied: PromoCodeGet | null,
  productId: number,
  unitPrice: number,
  subtotal: number
): { promo: PromoCodeGet | null; discount: number } => {
  const lines = getLinePromos(promos, applied);
  let bestPromo: PromoCodeGet | null = null;
  let bestDiscount = 0;
  for (const promo of lines) {
    if (!promo.isActive) continue;
    if (promo.appliesToOrderTotal) continue;
    if (!promo.productPromoCodes?.some((pp) => pp.productId === productId)) continue;
    if (!meetsMinOrder(promo, subtotal)) continue;
    const discount = computeLineDiscount(promo, unitPrice);
    if (discount > bestDiscount) {
      bestDiscount = discount;
      bestPromo = promo;
    }
  }
  return { promo: bestPromo, discount: bestDiscount };
};
